import React, { useEffect, useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { toast } from "../components/ui/sonner";
import Site from '../data/siteConfig.json';



/* 🔐 EMAILJS CONFIG */
const SERVICE_ID = 'service_j1zob1g';
const TEMPLATE_ID = 'template_dzukihp';
const PUBLIC_KEY = '1WBESfRQYfNaLcffj';

const HireUs: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    description: '',
  });
  
const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);

  emailjs
    .send(
      SERVICE_ID,
      TEMPLATE_ID,
      {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        projectType: formData.projectType,
        budget: formData.budget,
        description: formData.description,
      },
      PUBLIC_KEY
    )
    .then(() => {
      toast.success("Thank you for your interest!", {
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        projectType: "",
        budget: "",
        description: "",
      });
    })
    .catch((error) => {
      console.error("EmailJS Error:", error);

      toast.error("Submission failed", {
        description: "Please try again later.",
      });
    })
    .finally(() => setLoading(false));
};


  return (
    <>
      <SEO
        title="Hire Us"
        description="Looking for professional web development services? Contact TheCodeBazaar for custom website solutions."
      />

      <div className="bg-gradient-to-br from-sky-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Let's Build Something Amazing
              </h1>
              <p className="text-xl text-gray-600">
                Tell us about your project and we'll provide a custom solution
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Project Details
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Type *
                    </label>
                    <Select
                      value={formData.projectType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, projectType: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new-website">New Website</SelectItem>
                        <SelectItem value="redesign">Website Redesign</SelectItem>
                        <SelectItem value="ecommerce">E-commerce Store</SelectItem>
                        <SelectItem value="blog">Blog / Content Site</SelectItem>
                        <SelectItem value="portfolio">Portfolio Site</SelectItem>
                        <SelectItem value="custom">Custom Solution</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Range *
                    </label>
                    <Select
                      value={formData.budget}
                      onValueChange={(value) =>
                        setFormData({ ...formData, budget: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2k-5k">Rs 2,000 - 5,000</SelectItem>
                        <SelectItem value="5k-10k">Rs 5,000 - 10,000</SelectItem>
                        <SelectItem value="10k-20k">Rs 10,000 - 25,000</SelectItem>
                        <SelectItem value="20k+">Rs 25,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Description *
                    </label>
                    <Textarea
                      required
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="min-h-32"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-6 text-lg"
                  >
                    <Send className="mr-2 w-5 h-5" />
                    {loading ? 'Submitting...' : 'Submit Project Request'}
                  </Button>
                </form>
              </div>

              {/* Info – unchanged */}
              <div>
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-8 border mb-8">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Us?</h3>
                  <ul className="space-y-4">
                    {[
                      'Expert team with 10+ years experience',
                      'High-quality, custom solutions',
                      'Transparent pricing & timeline',
                      'Ongoing support & maintenance',
                      '100% satisfaction guarantee',
                      'Fast turnaround times',
                    ].map((item, index) => (
                      <li key={index} className="flex gap-3">
                        <CheckCircle className="text-green-500" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-8 border shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Response Time</h3>
                  <p className="text-gray-600 mb-4">
                    We typically respond within 24 hours.
                  </p>
                  <div className="space-y-2 text-sm">
                    <div>Email: {Site.contact.email}</div>
                    <div>Phone: {Site.contact.phone}</div>
                    <div>WhatsApp: {Site.contact.phone}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HireUs;